import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { TSP } from '../../../core/models';

interface SelectOption<T = string> {
  value: T;
  label: string;
}

@Component({
  selector: 'app-create-portal-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatError,
    MatFormField,
    MatIcon,
    MatSuffix,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './create-portal-form.html',
  styleUrl: './create-portal-form.scss',
})
export class CreatePortalForm {
  private readonly dialogRef = inject(MatDialogRef<CreatePortalForm>);

  public localeOptions: SelectOption<TSP.Helpers.LocaleCode>[] = [
    { value: 'en', label: 'English' },
    { value: 'uk', label: 'Українська' },
    { value: 'ru', label: 'Русский' },
  ];

  public subscriptionOptions: SelectOption<TSP.Internal.Recruiter.Portal.SubscriptionInfo['plan']>[] = [
    { value: 'trial', label: 'Пробний' },
    { value: 'free', label: 'Безкоштовний' },
    { value: 'professional', label: 'Професійний' },
    { value: 'team', label: 'Командний' },
    { value: 'enterprise', label: 'Корпоративний' },
  ];

  public aiCvParserOptions: SelectOption<TSP.Internal.Recruiter.Portal.AiCvParserAccessLevel>[] = [
    { value: TSP.Internal.Recruiter.Portal.AiCvParserAccessLevel.NONE, label: 'Без доступу' },
    { value: TSP.Internal.Recruiter.Portal.AiCvParserAccessLevel.FULL, label: 'Повний (OpenAI)' },
  ];

  public form = new FormGroup({
    name: new FormControl<string | null>(null, [Validators.required]),
    email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
    firstName: new FormControl<string | null>(null, [Validators.required]),
    lastName: new FormControl<string | null>(null),
    phone: new FormControl<string | null>(null),
    locale: new FormControl<TSP.Helpers.LocaleCode>('en', [Validators.required]),
    jobBoardUrl: new FormControl<string | null>(null),
    aiCvParserAccess: new FormControl<TSP.Internal.Recruiter.Portal.AiCvParserAccessLevel | null>(null, [
      Validators.required,
    ]),
    subscription: new FormControl<TSP.Internal.Recruiter.Portal.SubscriptionInfo['plan'] | null>(null, [
      Validators.required,
    ]),
    endDate: new FormControl<Date | null>(null),
  });

  public minEndDate = new Date();

  public onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.value;
    const value: TSP.Internal.Recruiter.Portal.CreatePayload = {
      name: raw.name,
      email: raw.email,
      firstName: raw.firstName,
      lastName: raw.lastName,
      phone: raw.phone,
      locale: raw.locale,
      jobBoardUrl: raw.jobBoardUrl,
      aiCvParserAccess: raw.aiCvParserAccess,
      subscription: raw.subscription,
      endDate: raw.endDate,
    };

    this.dialogRef.close(value);
  }
}
